// bundle-product-manager.js
// Main manager for bundle functionality: item selection, pricing, cart submission

if (!customElements.get('bundle-product-manager')) {
  customElements.define('bundle-product-manager', class BundleProductManager extends HTMLElement {
    constructor() {
      super();
      this.minItems = parseInt(this.dataset.minItems || 2);
      this.maxItems = parseInt(this.dataset.maxItems || 5);
      this.temporaryBundle = new Map();
      this.originalSlots = new Map();
      
      this.productItems = null;
      this.cartNotification = null;
      this.cartItems = null;
      this.bundleTotalPrice = null;
      this.addToCartAllButton = null;
      this.selectedSection = null;
      
      this.modalHandler = null;
      this.sliderHandler = null;
      
      this.initializeElements();
      this.setupEventListeners();
      this.saveOriginalSlots();
      this.updateTotalPrice();
      this.checkMinimumItems();
    }

    initializeElements() {
      this.bundleTotalPrice = this.querySelector('.bundle-total-price');
      this.addToCartAllButton = this.querySelector('.bundle-product-submit-button');
      this.productItems = this.querySelectorAll('product-bundle-variant-item');
      this.cartNotification = document.querySelector('cart-notification');
      this.cartItems = document.querySelector('cart-items');
      
      // Select the correct container based on maxItems
      const selectedItemsSection = this.querySelector('#selected-items-section');
      
      if (this.maxItems > 3) {
        // In slider mode, selectedSection is the .swiper container
        this.selectedSection = selectedItemsSection?.closest('.swiper');
      } else {
        // In grid mode, selectedSection is the .bundle-selected-items container
        this.selectedSection = selectedItemsSection;
      }
      
      this.modalHandler = new BundleModalHandler(this);
      this.sliderHandler = new BundleSliderHandler(this);
    }

    connectedCallback() {
      this.initializeElements();
      this.saveOriginalSlots();
      this.updateTotalPrice();
      this.checkMinimumItems();
      
      // Only initialize slider if maxItems > 3
      if (this.maxItems > 3) {
        requestAnimationFrame(() => {
          this.sliderHandler.selectedItemSlider();
        });
      }
    }

    saveOriginalSlots() {
      if (!this.selectedSection) return;
      
      this.selectedSection.querySelectorAll('.bundle-selected-card[available]').forEach(slot => {
        const index = slot.getAttribute('data-slot-index');
        if (index) this.originalSlots.set(index, slot.innerHTML);
      });
    }

    setupEventListeners() {
      this.addToCartAllButton?.addEventListener('click', this.handleSubmit.bind(this));
      document.addEventListener('bundle:variantSelected', this.handleVariantSelected.bind(this));
      this.addEventListener('click', this.handleBundleClick.bind(this));
      document.addEventListener('bundle:addFromModal', this.handleAddFromModalEvent.bind(this));
      document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this));
      
      this.modalHandler.setupModalEventListeners();
    }

    handleSectionLoad(event) {
      const section = event.detail.target || event.target;
      if (section?.querySelector('bundle-product-manager') === this || section === this.closest('.shopify-section')) {
        this.connectedCallback();
        requestAnimationFrame(() => {
          this.updateAddButtons();
        });
      }
    }

    handleBundleClick(event) {
      const target = event.target;
      
      if (target.classList.contains('add-to-bundle-variant') || target.closest('.add-to-bundle-variant')) {
        this.handleAddToBundle(event);
      } else if (target.classList.contains('product-add-bundle') || target.closest('.product-add-bundle')) {
        this.handleDirectAddToBundle(event);
      } else if (target.classList.contains('remove-from-bundle') || target.closest('.remove-from-bundle')) {
        this.handleRemoveFromBundle(event);
      }
    }

    handleVariantSelected(event) {
      const { productHandle, variant } = event.detail;
    }

    handleAddToBundle(event) {
      const button = event.target.closest('.add-to-bundle-variant');
      const productItem = button?.closest('.product-card');
      if (!productItem) return;

      // Prevent adding sold-out products
      if (button.disabled) return;

      const productHandle = productItem.dataset.productHandle;
      if (!productHandle) return;

      if (this.temporaryBundle.size >= this.maxItems) {
        alert(`You can only add up to ${this.maxItems} items to this bundle.`);
        return;
      }

      button.classList.add('loading');
      button.disabled = true;

      this.modalHandler.setLastFocusedElement(button);
      this.modalHandler.openVariantModal(productHandle, button);
    }

    handleDirectAddToBundle(event) {
      const button = event.target;
      const productItem = button.closest('.product-card');
      if (!productItem) return;

      // Prevent adding sold-out products
      if (button.disabled) return;

      const productHandle = productItem.dataset.productHandle;
      const variantId = button.dataset.id || productItem.querySelector('input[name="id"]')?.value;
      const price = parseFloat(button.dataset.price || productItem.querySelector('input[name="id"]')?.dataset.price || 0);

      if (!productHandle || !variantId) return;

      if (this.temporaryBundle.size >= this.maxItems) {
        alert(`You can only add up to ${this.maxItems} items to this bundle.`);
        return;
      }

      if (this.temporaryBundle.has(productHandle)) return;

      const title = productItem.querySelector('h3')?.textContent || 'Product';
      const variant = {
        id: variantId,
        price: price,
        title: 'Default Title',
        available: true,
        product_title: title
      };

      this.addItemToBundle(productHandle, { variant, element: productItem });
      productItem.classList.add('selected');
      button.disabled = true;
    }

    handleAddFromModalEvent(event) {
      const { variant, productHandle } = event.detail;

      if (!variant || !productHandle) {
        console.error('Missing variant or product handle from modal event');
        return;
      }

      // Prevent adding sold-out variants
      if (!variant.available) {
        console.error('Cannot add sold-out variant to bundle');
        return;
      }

      this.productItems = this.querySelectorAll('product-bundle-variant-item');
      const productItem = Array.from(this.productItems).find(item => item.dataset.productHandle === productHandle);

      if (!productItem) {
        console.error('Product item not found for handle:', productHandle);
        return;
      }

      this.addItemToBundle(productHandle, {
        variant: {
          id: parseInt(variant.id),
          price: parseInt(variant.price) || 0,
          title: variant.title,
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3,
          available: variant.available,
          featured_image: variant.featured_image
        },
        element: productItem
      });

      const addButton = productItem.querySelector('.add-to-bundle-variant, .product-add-bundle');
      if (addButton) {
        addButton.disabled = true;
        productItem.classList.add('selected');
      }

      this.modalHandler.focusNextAvailableProduct(productItem, this.productItems);
      this.modalHandler.closeModal();
    }

    handleRemoveFromBundle(event) {
      event.preventDefault();
      event.stopPropagation();

      const button = event.target.classList.contains('remove-from-bundle')
        ? event.target
        : event.target.closest('.remove-from-bundle');

      if (!button) return;

      const productHandle = button.dataset.handle;
      if (!productHandle) return;

      this.removeItemFromBundle(productHandle);
    }

    addItemToBundle(productHandle, data) {
      if (this.temporaryBundle.has(productHandle)) return;

      data.quantity = 1;
      this.temporaryBundle.set(productHandle, data);
      this.renderSelectedItem(productHandle, data);

      this.updateTotalPrice();
      this.checkMinimumItems();

      requestAnimationFrame(() => {
        this.updateAddButtons();
      });
    }

    removeItemFromBundle(productHandle) {
      if (!this.temporaryBundle.get(productHandle)) return;

      this.temporaryBundle.delete(productHandle);

      const productItem = Array.from(this.productItems).find(item => item.dataset.productHandle === productHandle);
      if (productItem) {
        productItem.classList.remove('selected');
        const addButton = productItem.querySelector('.add-to-bundle-variant, .product-add-bundle');
        if (addButton) {
          // Only re-enable if not sold out (check for sold-out class or text)
          const isSoldOut = addButton.textContent.trim().toLowerCase().includes('sold out') ||
                            addButton.classList.contains('sold-out');
          if (!isSoldOut) {
            addButton.disabled = false;
          }
          addButton.classList.remove('loading');
        }
      }

      this.reorganizeSlots();
      this.updateTotalPrice();
      this.checkMinimumItems();

      requestAnimationFrame(() => {
        this.updateAddButtons();
      });
    }

    reorganizeSlots() {
      if (!this.selectedSection) return;

      const isSliderMode = this.maxItems > 3;
      const filledSlots = [];

      this.temporaryBundle.forEach((bundleData, productHandle) => {
        filledSlots.push({ productHandle, bundleData });
      });

      if (isSliderMode) {
        const swiperWrapper = this.selectedSection.querySelector('.swiper-wrapper');
        if (swiperWrapper) swiperWrapper.innerHTML = '';
      } else {
        this.selectedSection.innerHTML = '';
      }

      const totalSlots = Math.max(3, filledSlots.length);

      for (let i = 0; i < totalSlots; i++) {
        const slotData = filledSlots[i];
        const slot = document.createElement('div');
        slot.className = 'bundle-selected-card';
        slot.setAttribute('data-section-id', 'bundle-product-card');
        slot.setAttribute('data-slot-index', i + 1);

        if (slotData) {
          slot.setAttribute('data-product-handle', slotData.productHandle);
        } else {
          slot.setAttribute('available', '');
        }

        slot.innerHTML = this.getOriginalPlaceholderHTML();

        if (isSliderMode) {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide';
          slide.appendChild(slot);
          this.selectedSection.querySelector('.swiper-wrapper').appendChild(slide);
        } else {
          this.selectedSection.appendChild(slot);
        }
      }

      const fetchPromises = filledSlots.map((slotData, index) => {
        return fetch(`/products/${slotData.productHandle}?section_id=bundle-product-card`)
          .then(response => response.text())
          .then(html => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;

            const card = tempDiv.querySelector('[data-section-id="bundle-product-card"]');
            if (!card) return;

            const targetSlot = this.selectedSection.querySelector(`[data-product-handle="${slotData.productHandle}"]`);
            if (!targetSlot) return;

            targetSlot.removeAttribute('available');
            targetSlot.innerHTML = card.innerHTML;

            this.updateSelectedItem(targetSlot, slotData.bundleData.variant);

            const removeButton = targetSlot.querySelector('.remove-from-bundle');
            if (removeButton) {
              removeButton.dataset.handle = slotData.productHandle;
              removeButton.dataset.slotIndex = index + 1;
            }

            this.setupQuantityInput(targetSlot, slotData.productHandle);

            if (slotData.bundleData.quantity > 1) {
              const quantityInput = targetSlot.querySelector('.bundle-quantity-input');
              if (quantityInput) quantityInput.value = slotData.bundleData.quantity;
            }
          })
          .catch(error => console.error('Error loading slot:', error));
      });

      Promise.all(fetchPromises).then(() => {
        if (isSliderMode) {
          requestAnimationFrame(() => {
            this.sliderHandler.selectedItemSlider();
          });
        }
      });
    }

    getOriginalPlaceholderHTML() {
      return '<div class="bundle__horizontal-product-media placeholder"></div><div class="bundle-selected-info"><h4 class="bundle-selected-title"></h4><div class="price"></div></div>';
    }

    renderSelectedItem(productHandle, { variant, element }) {
      if (!this.selectedSection) {
        console.error('selectedSection not found');
        return;
      }

      const existingItem = this.selectedSection.querySelector(`[data-product-handle="${productHandle}"]`);
      if (existingItem) {
        this.updateSelectedItem(existingItem, variant);
        return;
      }

      const isSliderMode = this.maxItems > 3;
      let targetSlot = this.selectedSection.querySelector('.bundle-selected-card[available]');

      if (!targetSlot) {
        this.reorganizeSlots();
        return;
      }

      fetch(`/products/${productHandle}?section_id=bundle-product-card`)
        .then(response => response.text())
        .then(html => {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          const card = tempDiv.querySelector('[data-section-id="bundle-product-card"]');
          if (!card) return;

          const slotIndex = targetSlot.getAttribute('data-slot-index');
          targetSlot.removeAttribute('available');
          targetSlot.setAttribute('data-product-handle', productHandle);
          targetSlot.innerHTML = card.innerHTML;

          this.updateSelectedItem(targetSlot, variant);

          const removeButton = targetSlot.querySelector('.remove-from-bundle');
          if (removeButton) {
            removeButton.dataset.handle = productHandle;
            removeButton.dataset.slotIndex = slotIndex;
          }

          this.setupQuantityInput(targetSlot, productHandle);

          if (isSliderMode) {
            requestAnimationFrame(() => {
              this.sliderHandler.selectedItemSlider();
            });
          }
        })
        .catch(error => {
          console.error('Error rendering selected item:', error);
        });
    }

    setupQuantityInput(card, productHandle) {
      const quantityInput = card.querySelector('.bundle-quantity-input');
      if (!quantityInput) return;

      const quantityWrapper = card.querySelector('.bundle-quantity-wrapper');
      const minusButton = quantityWrapper?.querySelector('.quantity__button[name="minus"]');
      const plusButton = quantityWrapper?.querySelector('.quantity__button[name="plus"]');

      const updateQuantity = (delta) => {
        const currentValue = parseInt(quantityInput.value) || 1;
        const maxValue = quantityInput.max ? parseInt(quantityInput.max) : 999;
        const newValue = Math.max(1, Math.min(maxValue, currentValue + delta));
        quantityInput.value = newValue;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      };

      if (minusButton) {
        const newMinusButton = minusButton.cloneNode(true);
        minusButton.parentNode.replaceChild(newMinusButton, minusButton);
        newMinusButton.addEventListener('click', (e) => {
          e.preventDefault();
          if (parseInt(quantityInput.value) > 1) updateQuantity(-1);
        });
      }

      if (plusButton) {
        const newPlusButton = plusButton.cloneNode(true);
        plusButton.parentNode.replaceChild(newPlusButton, plusButton);
        newPlusButton.addEventListener('click', (e) => {
          e.preventDefault();
          updateQuantity(1);
        });
      }

      quantityInput.addEventListener('change', (e) => {
        const value = Math.max(1, Math.min(parseInt(e.target.max) || 999, parseInt(e.target.value) || 1));
        e.target.value = value;
        this.updateBundleItemQuantity(productHandle, value);
      });
    }

    updateBundleItemQuantity(productHandle, quantity) {
      const bundleData = this.temporaryBundle.get(productHandle);
      if (bundleData) {
        bundleData.quantity = quantity;
        this.updateTotalPrice();
      }
    }

    updateSelectedItem(element, variant) {
      if (!element || !variant) return;

      const variantContainer = element.querySelector('.bundle-selected-variant');
      const variantTitle = element.querySelector('.variant-title');

      // Only show variant info if the product has variants (not "Default Title")
      if (variantContainer) {
        if (variant.title && variant.title !== 'Default Title') {
          variantContainer.style.display = '';
          if (variantTitle) {
            variantTitle.textContent = variant.title;
          }
        } else {
          variantContainer.style.display = 'none';
        }
      }

      const priceElement = element.querySelector('.price');
      if (priceElement && variant.price) {
        priceElement.textContent = this.formatMoney(variant.price, { showCurrencyCode: false });
      }

      const hiddenInput = element.querySelector('input[name="id"]');
      if (hiddenInput) {
        hiddenInput.value = variant.id;
        if (variant.price) hiddenInput.dataset.price = variant.price;
        hiddenInput.dataset.variantTitle = variant.title;
        if (variant.option1) hiddenInput.dataset.option1 = variant.option1;
        if (variant.option2) hiddenInput.dataset.option2 = variant.option2;
        if (variant.option3) hiddenInput.dataset.option3 = variant.option3;
      }
    }

    updateTotalPrice() {
      if (!this.bundleTotalPrice) return;

      let total = 0;
      this.temporaryBundle.forEach(({ variant, quantity }) => {
        if (variant?.price) total += parseInt(variant.price) * (quantity || 1);
      });

      this.bundleTotalPrice.textContent = this.temporaryBundle.size > 0
        ? this.formatMoney(total, { showCurrencyCode: true })
        : this.formatMoney(0, { showCurrencyCode: true });
    }

    get translations() {
      return {
        selectMoreItems: this.dataset.tSelectMoreItems || '*Select {count} more {item_text} to create bundle',
        item: this.dataset.tItem || 'item',
        items: this.dataset.tItems || 'items',
        readyToAdd: this.dataset.tReadyToAdd || '✓ Ready to add to cart!'
      };
    }
    getTranslation(key, replacements = {}) {
      let text = this.translations[key] || key;
      Object.keys(replacements).forEach(placeholder => {
        text = text.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), replacements[placeholder]);
      });
      return text;
    }
    checkMinimumItems() {
      const selectedCount = this.temporaryBundle.size;
      const notice = this.querySelector('.bundle-minimum-notice');
      const footer = this.querySelector('.bundle-footer');
      if (!notice) return;
      if (selectedCount < this.minItems) {
        const remainingItems = this.minItems - selectedCount;
        const itemText = remainingItems === 1 
          ? this.translations.item 
          : this.translations.items;
        notice.innerHTML = this.getTranslation('selectMoreItems', {
          count: remainingItems,
          item_text: itemText
        });
        notice.style.color = '#e74c3c';
        footer?.classList.remove('ready');
        this.disableAddToCartButton();
      } else {
        notice.innerHTML = this.translations.readyToAdd;
        notice.style.color = '#28a745';
        footer?.classList.add('ready');
        this.enableAddToCartButton();
      }
    }

    updateAddButtons() {
      const allProductItems = this.querySelectorAll('product-bundle-variant-item');
      const isBundleFull = this.temporaryBundle.size >= this.maxItems;

      allProductItems.forEach(item => {
        const productHandle = item.dataset.productHandle;
        const isInBundle = this.temporaryBundle.has(productHandle);
        const addButtons = item.querySelectorAll('.add-to-bundle-variant, .product-add-bundle');

        addButtons.forEach(addButton => {
          // Check if product is sold out (never re-enable sold-out buttons)
          const isSoldOut = addButton.textContent.trim().toLowerCase().includes('sold out') ||
                            addButton.classList.contains('sold-out');

          if (isSoldOut) {
            // Always keep sold-out items disabled
            addButton.disabled = true;
            addButton.classList.remove('loading');
            item.classList.remove('selected');
          } else if (isInBundle) {
            addButton.disabled = true;
            addButton.classList.remove('loading');
            item.classList.add('selected');
          } else if (isBundleFull) {
            addButton.disabled = true;
            addButton.classList.remove('loading');
            item.classList.remove('selected');
          } else {
            addButton.disabled = false;
            addButton.classList.remove('loading');
            item.classList.remove('selected');
          }
        });
      });
    }

    enableAddToCartButton() {
      if (this.addToCartAllButton) {
        this.addToCartAllButton.disabled = false;
        this.addToCartAllButton.classList.remove('disabled');
      }
    }

    disableAddToCartButton() {
      if (this.addToCartAllButton) {
        this.addToCartAllButton.disabled = true;
        this.addToCartAllButton.classList.add('disabled');
      }
    }

    formatMoney(cents, options = {}) {
      if (typeof cents === 'string') cents = cents.replace('.', '');

      const value = parseInt(cents, 10) || 0;

      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(value, theme.moneyFormat || '${{amount}}');
      }

      const amount = value / 100;
      const currency = Shopify?.currency?.active || 'USD';

      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);

      return options.showCurrencyCode === true ? `${formatted} ${currency}` : formatted;
    }

    handleSubmit(event) {
      event.preventDefault();

      if (this.temporaryBundle.size < this.minItems) {
        alert(`Please select at least ${this.minItems} items for the bundle`);
        return;
      }

      const selectedItems = [];
      const isSliderMode = this.maxItems > 3;

      this.temporaryBundle.forEach(({ variant, quantity }, productHandle) => {
        if (!variant?.id) return;

        const variantId = parseInt(variant.id);
        if (isNaN(variantId)) return;

        let card;
        if (isSliderMode) {
          card = this.selectedSection?.querySelector(`.swiper-slide .bundle-selected-card[data-product-handle="${productHandle}"]`);
        } else {
          card = this.selectedSection?.querySelector(`.bundle-selected-card[data-product-handle="${productHandle}"]`);
        }

        let itemQuantity = quantity || 1;

        if (card) {
          const quantityInput = card.querySelector('.bundle-quantity-input');
          if (quantityInput) {
            const inputValue = parseInt(quantityInput.value);
            if (!isNaN(inputValue) && inputValue > 0) {
              itemQuantity = inputValue;
            }
          }
        }

        selectedItems.push({
          id: variantId,
          quantity: Math.max(1, itemQuantity),
          productHandle,
          variantTitle: variant.title || 'Unknown'
        });
      });

      if (selectedItems.length === 0) {
        alert('No valid items selected for bundle');
        return;
      }

      this.addToCartAllButton.classList.add('loading');
      this.addToCartAllButton.disabled = true;

      let sectionsToRender = [];
      if (this.cartNotification) {
        sectionsToRender = this.cartNotification.getSectionsToRender().map(section => section.id);
        this.cartNotification.setActiveElement(document.activeElement);
      }

      const cartItems = selectedItems.map(({ id, quantity }) => ({ id, quantity }));

      fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: cartItems,
          sections: sectionsToRender,
          section_url: window.location.pathname
        })
      })
        .then(response => response.json())
        .then(data => {
          if (data.status && data.status !== 'success') {
            throw new Error(data.description || 'Error adding items to cart');
          }

          this.cartNotification?.renderContents(data);
          this.cartItems?.renderContents(data);
          this.resetBundle();
        })
        .catch(error => {
          console.error('Error adding bundle to cart:', error);
          alert(error.message || 'There was an error adding the bundle to your cart');
        })
        .finally(() => {
          this.addToCartAllButton.classList.remove('loading');
          this.addToCartAllButton.disabled = false;
        });
    }

    resetBundle() {
      this.temporaryBundle.clear();

      this.productItems.forEach(item => {
        item.classList.remove('selected');
        const addButton = item.querySelector('.add-to-bundle-variant, .product-add-bundle');
        if (addButton) {
          // Only re-enable if not sold out
          const isSoldOut = addButton.textContent.trim().toLowerCase().includes('sold out') ||
                            addButton.classList.contains('sold-out');
          if (!isSoldOut) {
            addButton.disabled = false;
          }
          addButton.classList.remove('loading');
        }
      });

      const isSliderMode = this.maxItems > 3;

      if (this.selectedSection) {
        if (isSliderMode) {
          const swiperWrapper = this.selectedSection.querySelector('.swiper-wrapper');
          if (swiperWrapper) swiperWrapper.innerHTML = '';

          for (let i = 0; i < 3; i++) {
            const slide = document.createElement('div');
            slide.className = 'swiper-slide';

            const slot = document.createElement('div');
            slot.className = 'bundle-selected-card';
            slot.setAttribute('data-section-id', 'bundle-product-card');
            slot.setAttribute('data-slot-index', i + 1);
            slot.setAttribute('available', '');
            slot.innerHTML = this.getOriginalPlaceholderHTML();

            slide.appendChild(slot);
            swiperWrapper.appendChild(slide);
          }

          requestAnimationFrame(() => {
            this.sliderHandler.selectedItemSlider();
          });
        } else {
          this.selectedSection.innerHTML = '';

          for (let i = 0; i < 3; i++) {
            const slot = document.createElement('div');
            slot.className = 'bundle-selected-card';
            slot.setAttribute('data-section-id', 'bundle-product-card');
            slot.setAttribute('data-slot-index', i + 1);
            slot.setAttribute('available', '');
            slot.innerHTML = this.getOriginalPlaceholderHTML();
            this.selectedSection.appendChild(slot);
          }
        }
      }

      this.updateTotalPrice();
      this.checkMinimumItems();
      this.updateAddButtons();
    }
  });
}